import { BillingProfile, UserBillDetail } from '../../types/rest-api';

/**
 * Values a client sends when it has nothing real to send. The backend refuses
 * these outright (`UserBillDetail`); we mirror the list so an address already
 * stored as a placeholder is treated as no address at all rather than as a
 * profile that is complete.
 */
const PLACEHOLDERS = new Set([
  '-',
  '--',
  '---',
  '–',
  '—',
  '.',
  '..',
  'n/a',
  'na',
  'none',
  'null',
]);

function isMeaningful(value: string | null | undefined): boolean {
  const v = (value ?? '').trim();
  return v.length > 0 && !PLACEHOLDERS.has(v.toLowerCase());
}

/**
 * Addresses nobody. Was the purchase path's fallback whenever the account had
 * no e-mail, and the backend now refuses it -- so treating it as a valid
 * identity here would only turn "ask the user" into an unexplained 400.
 */
const PLACEHOLDER_EMAILS = new Set(['noreply@bildigsnetz.ch']);
const PLACEHOLDER_ORGANIZATION_NAMES = new Set([
  'organization',
  'organisation',
]);

/** Stripe needs these two to address an invoice at all. */
export function hasBillingIdentity(profile: BillingProfile | null): boolean {
  if (!profile) return false;
  const name = (profile.organization_name ?? '').trim().toLowerCase();
  const email = (profile.email ?? '').trim().toLowerCase();
  return (
    isMeaningful(profile.organization_name) &&
    !PLACEHOLDER_ORGANIZATION_NAMES.has(name) &&
    isMeaningful(profile.email) &&
    !PLACEHOLDER_EMAILS.has(email)
  );
}

/**
 * Whether an invoice can actually be posted to this profile.
 *
 * Deliberately stricter than "a billing account row exists": an organisation
 * created on the free plan already has one, holding a name and an e-mail and no
 * address whatsoever. That row is why we cannot use presence to decide whether
 * to ask.
 */
export function hasInvoiceableAddress(profile: BillingProfile | null): boolean {
  return (
    hasBillingIdentity(profile) &&
    isMeaningful(profile!.street) &&
    isMeaningful(profile!.zipcode) &&
    isMeaningful(profile!.city)
  );
}

/**
 * The profile a checkout form describes.
 *
 * Inverse of `toBillDetail` in {@link CheckoutBillingService}, and a mirror of
 * the backend's `profile_from_bill_detail` (billing_organisation.py): the order
 * API still wants a first/last name pair where the profile keeps one free-text
 * contact, so the pair is joined back together here.
 */
export function profileFromBillDetail(bill: UserBillDetail): BillingProfile {
  return {
    organization_name: bill.organization_name,
    contact_name: `${bill.first_name ?? ''} ${bill.last_name ?? ''}`.trim(),
    email: bill.email,
    phone: bill.phone ?? null,
    street: bill.street ?? null,
    city: bill.city ?? null,
    zipcode: bill.zipcode ?? null,
    tax_id: bill.tax_id ?? null,
  };
}

/** Filled in from a checkout only while still blank -- see {@link fillMissingProfileFields}. */
const FILLABLE_PROFILE_FIELDS = [
  'street',
  'city',
  'zipcode',
  'phone',
  'tax_id',
] as const;

/**
 * Complete a stored profile from checkout details, never overwriting one.
 *
 * Deliberately identical to the backend's `fill_missing_profile_fields`
 * (billing_organisation.py): anything already stored was either typed in the
 * settings screen or read back from Stripe, and both beat a checkout form. The
 * merge has to happen here because `PUT .../billing-profile` replaces the whole
 * profile, unlike the order path which fills blanks server-side.
 *
 * Returns null when there is nothing to add.
 */
export function fillMissingProfileFields(
  stored: BillingProfile,
  fromCheckout: BillingProfile
): BillingProfile | null {
  const updates: Partial<BillingProfile> = {};
  for (const field of FILLABLE_PROFILE_FIELDS) {
    const current = (stored[field] ?? '').trim();
    const incoming = (fromCheckout[field] ?? '').trim();
    if (!current && incoming) updates[field] = incoming;
  }
  return Object.keys(updates).length ? { ...stored, ...updates } : null;
}

/** Empty starting point for the checkout form. */
export function emptyBillingProfile(
  seed: Partial<BillingProfile> = {}
): BillingProfile {
  return {
    organization_name: '',
    contact_name: null,
    email: '',
    phone: null,
    street: null,
    city: null,
    zipcode: null,
    tax_id: null,
    ...seed,
  };
}

/** The one spelling Stripe's `ch_vat` accepts; also what the field shows as a hint. */
export const SWISS_VAT_EXAMPLE = 'CHE-123.456.789 MWST';

/** Kept identical to the backend's message, so both routes read the same. */
export const SWISS_VAT_ERROR = `MwSt-Nr. ungültig. Erwartet: ${SWISS_VAT_EXAMPLE}`;

const SWISS_VAT_RE = /^(?:CHE)?(\d{9})(MWST|TVA|IVA)?$/;

/**
 * Canonical form of a Swiss VAT number, or `null` when there is none.
 *
 * Mirrors the backend's normalisation so the user finds out here, while the
 * field is still in front of them, rather than from a 400. Returns `null` for
 * blank input because the number is optional, and `undefined` when the input
 * cannot be a Swiss VAT number at all.
 */
export function normalizeSwissVat(
  value: string | null | undefined
): string | null | undefined {
  const compact = (value ?? '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();
  if (!compact) return null;

  const match = SWISS_VAT_RE.exec(compact);
  if (!match) return undefined;

  const digits = match[1];
  const suffix = match[2] ?? 'MWST';
  return `CHE-${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)} ${suffix}`;
}

/** The message to show under the MwSt-Nr. field, or `null` when it is fine. */
export function swissVatError(value: string | null | undefined): string | null {
  return normalizeSwissVat(value) === undefined ? SWISS_VAT_ERROR : null;
}
